import React from "react"
import Menu from "@material-ui/core/Menu"
import Button from "@material-ui/core/Button"
import ListItem from "@material-ui/core/ListItem"
import ListItemText from "@material-ui/core/ListItemText"
import Tooltip from "@material-ui/core/Tooltip"
import Typography from "@material-ui/core/Typography"
import ProjectMenu from "./ProjectMenu"
import ProjectChatButton from "./ProjectChatButton"
import { ProjectViewModel } from "../../api/project"
import { makeStyles, Theme, useTheme } from "@material-ui/core"
import { ChevronDown, ChevronLeft, ChevronRight } from "react-feather"
import { useUrlWithContext } from "../../../hooks/use-url-with-context"
import { paths } from "../../../paths"
import { Link, useLocation } from "react-router-dom"
import { useTranslations } from "./use-translations"
import { useCanViewReportedHoursTab } from "../../../hour-reports/hooks/use-can-view-reported-hours-tab"

const useMenuState = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl((prev) => (prev ? null : e.currentTarget))
  const closeMenu = () => setAnchorEl(null)

  return { anchorEl, toggleMenu, closeMenu }
}

const useProjectNavItems = ({ project }: { project: ProjectViewModel }) => {
  const { createPathWithContext } = useUrlWithContext()
  const translations = useTranslations()
  const location = useLocation()
  const canViewReportedHoursTab = useCanViewReportedHoursTab()

  const basicPath = paths.projectBasic(project.id)
  const kanbanPath = paths.projectKanban(project.id)
  const tasksPath = paths.projectTasks(project.id)
  const todosPath = paths.projectTodos(project.id)
  const calendarPath = paths.projectCalendar(project.id)
  const reportedHoursPath = paths.projectReportedHours(project.id)
  const dashboardPath = paths.projectDashboard(project.id)

  const basicRoute = createPathWithContext(basicPath)
  const kanbanRoute = createPathWithContext(kanbanPath)
  const tasksRoute = createPathWithContext(tasksPath)
  const todosRoute = `${createPathWithContext(todosPath)}&view=all`
  const calendarRoute = createPathWithContext(calendarPath)
  const reportedHoursRoute = createPathWithContext(reportedHoursPath)
  const dashboardRoute = createPathWithContext(dashboardPath)
  const projectNavItems = [
    {
      id: basicPath,
      label: translations.basicInfoTabLabel,
      to: basicRoute,
    },
    {
      id: tasksPath,
      label: translations.tasksTabLabel,
      to: tasksRoute,
    },
    {
      id: todosPath,
      label: translations.todoListTabLabel,
      to: todosRoute,
    },
    {
      id: calendarPath,
      label: translations.calendarTabLabel,
      to: calendarRoute,
    },
    {
      id: kanbanPath,
      label: translations.kanbanTabLabel,
      to: kanbanRoute,
    },
    ...(canViewReportedHoursTab
      ? [
          {
            id: reportedHoursPath,
            label: translations.reportedHoursTabLabel,
            to: reportedHoursRoute,
          },
        ]
      : []),
    {
      id: dashboardPath,
      label: translations.dashboardTabLabel,
      to: dashboardRoute,
    },
  ]

  const activeTab = projectNavItems.find((item) => location.pathname.includes(item.id)) || projectNavItems[0]
  const activeTabIndex = projectNavItems.indexOf(activeTab)
  const prevTab = projectNavItems[activeTabIndex - 1] || null
  const nextTab = projectNavItems[activeTabIndex + 1] || null

  return {
    projectNavItems,
    activeTab,
    prevTab,
    nextTab,
  }
}

const ProjectNavbarMobile = ({ project }: { project: ProjectViewModel }) => {
  const classes = useStyles()
  const theme = useTheme()
  const { anchorEl, toggleMenu, closeMenu } = useMenuState()
  const { projectNavItems, activeTab, prevTab, nextTab } = useProjectNavItems({ project })

  return (
    <div className={classes.container}>
      <div className={classes.titleRow}>
        <Tooltip title={project.fullTitle}>
          <Typography variant="h4">{project.fullTitle}</Typography>
        </Tooltip>
        <div className={classes.titleRowActions}>
          <ProjectChatButton project={project} />
          <ProjectMenu project={project} />
        </div>
      </div>
      <div className={classes.navRow}>
        {prevTab ? (
          <Button
            startIcon={<ChevronLeft size={16} />}
            component={Link}
            to={prevTab.to}
            style={{ color: theme.palette.text.secondary }}
          >
            {prevTab.label}
          </Button>
        ) : null}
        <div style={{ flexGrow: 1, marginLeft: 8, marginRight: 8 }}>
          <Button
            onClick={toggleMenu}
            endIcon={<ChevronDown size={16} />}
            variant="outlined"
            style={{ background: theme.palette.background.paper }}
            fullWidth
          >
            {activeTab.label}
          </Button>
          <Menu id="simple-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={closeMenu}>
            {projectNavItems.map((item) => {
              return (
                <ListItem key={item.to} component={Link} to={item.to} button dense onClick={closeMenu}>
                  <ListItemText primary={item.label} />
                </ListItem>
              )
            })}
          </Menu>
        </div>
        {nextTab ? (
          <Button
            endIcon={<ChevronRight size={16} />}
            component={Link}
            to={nextTab.to}
            style={{ color: theme.palette.text.secondary }}
          >
            {nextTab.label}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(1, 1, 0, 1),
      marginBottom: theme.spacing(1.5),
      marginLeft: theme.spacing(0),
      marginRight: theme.spacing(0),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    titleRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "nowrap",
      "& h6": {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        maxWidth: "calc(100% - 48px)",
      },
      "& button": {
        backgroundColor: theme.palette.divider,
        borderRadius: theme.shape.borderRadius,
        marginLeft: theme.spacing(1),
      },
    },
    // Grouped so the buttons stay together at the end of the row instead of being
    // spread out by the row's space-between.
    titleRowActions: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
    },
    navRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2, 0, 1, 0),
      margin: theme.spacing(0, -1),
    },
    navButton: {
      border: `1px solid ${theme.palette.divider}`,
      display: "flex",
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    },
    navButtonLabel: {
      margin: theme.spacing(0, 1),
    },
  }
})

export default ProjectNavbarMobile
